import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { GWPField, GWPInput, GWPSelect, GWPTextarea } from 'shared-components';
import ApplicationSection from '../ApplicationSection';
import { EntertainerApplicationContract, EntertainerApplicationFormState } from '../EntertainerApplicationTypes';
import { fieldError, useApplicationFormUpdate } from '../EntertainerApplicationFormUtils';

type Props = { contract: EntertainerApplicationContract };

export default function RequirementsSection({ contract }: Props) {
	const { control, formState } = useFormContext<EntertainerApplicationFormState>();
	const update = useApplicationFormUpdate();
	const [company, policyNumber, expiry, powerSlots, requirements] = useWatch({
		control,
		name: [
			'EntertainerInsuranceCompany',
			'EntertainerInsurancePolicyNumber',
			'EntertainerInsuranceExpiry',
			'EntertainerPowerSlotsRequired',
			'EntertainerRequirements',
		],
	});
	const powerOptions = useMemo(() => {
		const { minimum, maximum } = contract.constraints.powerSlots;
		return Array.from({ length: maximum - minimum + 1 }, (_, index) => {
			const value = minimum + index;
			return { value: String(value), label: value === 0 ? 'None' : String(value) };
		});
	}, [contract.constraints.powerSlots]);

	return (
		<ApplicationSection
			title="Requirements and insurance"
			description="Help our event team plan a safe, practical setup."
		>
			<div className="entertainer-grid three-columns">
				<GWPField label="Insurance company">
					<GWPInput
						id="EntertainerInsuranceCompany"
						value={company}
						onValueChange={(value) => update('EntertainerInsuranceCompany', value)}
					/>
				</GWPField>
				<GWPField label="Policy number">
					<GWPInput
						id="EntertainerInsurancePolicyNumber"
						value={policyNumber}
						onValueChange={(value) => update('EntertainerInsurancePolicyNumber', value)}
					/>
				</GWPField>
				<GWPField label="Expiry date" error={fieldError(formState.errors, 'EntertainerInsuranceExpiry')}>
					<GWPInput
						id="EntertainerInsuranceExpiry"
						type="date"
						value={expiry}
						onValueChange={(value) => update('EntertainerInsuranceExpiry', value)}
					/>
				</GWPField>
			</div>
			<div className="entertainer-grid two-columns">
				<GWPField
					label="Power outlets required"
					hint="Select the number of standard outlets"
					error={fieldError(formState.errors, 'EntertainerPowerSlotsRequired')}
				>
					<GWPSelect
						id="EntertainerPowerSlotsRequired"
						value={powerSlots}
						options={powerOptions}
						onValueChange={(value) =>
							update(
								'EntertainerPowerSlotsRequired',
								value ?? String(contract.constraints.powerSlots.minimum),
							)
						}
					/>
				</GWPField>
				<GWPField label="Other requirements" hint="Space, access, sound or setup needs">
					<GWPTextarea
						id="EntertainerRequirements"
						value={requirements}
						onValueChange={(value) => update('EntertainerRequirements', value)}
						rows={4}
					/>
				</GWPField>
			</div>
		</ApplicationSection>
	);
}
